import React, { useEffect, useRef } from 'react';
import { GiftIcon } from 'lucide-react';
export function Hero() {
  const leavesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Animation for falling leaves and sparkles
    const animateElements = () => {
      const elements = leavesRef.current?.querySelectorAll('.floating-element');
      if (!elements) return;
      elements.forEach(element => {
        const delay = Math.random() * 5;
        const duration = 3 + Math.random() * 4;
        const elementNode = element as HTMLElement;
        elementNode.style.animation = `float ${duration}s ease-in-out infinite ${delay}s`;
      });
    };
    animateElements();
  }, []);
  const scrollToForm = () => {
    const formElement = document.getElementById('gift-form');
    if (formElement) {
      formElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative py-20 px-6 md:py-28 overflow-hidden min-h-[600px]" data-id="element-98">
      {/* Solid warm red-orange background */}
      <div className="absolute inset-0 z-0" style={{
      background: '#BB3E03'
    }} data-id="element-99" />
      {/* Floating autumn leaves and sparkles */}
      <div ref={leavesRef} className="absolute inset-0 pointer-events-none" data-id="element-100">
        {/* Golden maple leaves */}
        {[...Array(12)].map((_, i) => {
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        const size = 28 + Math.random() * 20;
        const rotation = Math.random() * 360;
        const colors = ['#D4A574', '#C9A961', '#E6B87D', '#B8956A'];
        const color = colors[Math.floor(Math.random() * colors.length)];
        return <div key={`leaf-${i}`} className="absolute floating-element" style={{
          top: `${top}%`,
          left: `${left}%`,
          fontSize: `${size}px`,
          transform: `rotate(${rotation}deg)`,
          opacity: 0.6,
          color: color
        }} data-id="element-101">
              🍁
            </div>;
      })}
        {/* Oak leaf shapes */}
        {[...Array(8)].map((_, i) => {
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        const size = 24 + Math.random() * 16;
        const rotation = Math.random() * 360;
        const colors = ['#D4A574', '#C9A961', '#E6B87D'];
        const color = colors[Math.floor(Math.random() * colors.length)];
        return <div key={`oak-${i}`} className="absolute floating-element" style={{
          top: `${top}%`,
          left: `${left}%`,
          fontSize: `${size}px`,
          transform: `rotate(${rotation}deg)`,
          opacity: 0.5,
          color: color
        }} data-id="element-102">
              🍂
            </div>;
      })}
        {/* Small sparkle dots */}
        {[...Array(15)].map((_, i) => {
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        const size = 8 + Math.random() * 8;
        return <div key={`dot-${i}`} className="absolute floating-element" style={{
          top: `${top}%`,
          left: `${left}%`,
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          background: '#E6B87D',
          opacity: 0.5
        }} data-id="element-103" />;
      })}
        {/* Star sparkles */}
        {[...Array(8)].map((_, i) => {
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        const size = 16 + Math.random() * 12;
        return <div key={`star-${i}`} className="absolute floating-element" style={{
          top: `${top}%`,
          left: `${left}%`,
          fontSize: `${size}px`,
          opacity: 0.5,
          color: '#E6B87D'
        }} data-id="element-104">
              ✨
            </div>;
      })}
        {/* Four-point stars */}
        {[...Array(6)].map((_, i) => {
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        const size = 18 + Math.random() * 14;
        return <div key={`fourpoint-${i}`} className="absolute floating-element" style={{
          top: `${top}%`,
          left: `${left}%`,
          fontSize: `${size}px`,
          opacity: 0.4,
          color: '#D4A574'
        }} data-id="element-105">
              ✦
            </div>;
      })}
      </div>
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-106">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-107">
          {/* Left side - Text content */}
          <div className="text-white md:w-1/2" data-id="element-108">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-109">
              Share the Gift of Leadership Wisdom
            </h1>
            <p className="text-lg md:text-xl mb-6 leading-relaxed" data-id="element-110">
              'Tis the season of giving, and we have an exceptional opportunity
              for you to share the gift of knowledge with your friends, family
              and colleagues.
            </p>
            <p className="text-base md:text-lg mb-10 leading-relaxed" data-id="element-111">
              We will send free print copies of Applied Wisdom for the Nonprofit
              Sector to as many people as you choose. Shipping is included.
            </p>
            <button onClick={scrollToForm} className="bg-[#E85C41] hover:bg-[#d04b32] text-white font-semibold py-3 px-8 rounded-md text-base transition-colors flex items-center gap-2 shadow-lg" data-id="element-112">
              <GiftIcon size={20} data-id="element-113" />
              Send Free Copies
            </button>
          </div>
          {/* Right side - Clean image */}
          <div className="md:w-1/2 flex justify-center items-center" data-id="element-114">
            <div className="relative transform transition-all duration-500 hover:scale-105" data-id="element-115">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hu9ZpkQH1tmU6vzRYr3Af2/image001.jpg" alt="Applied Wisdom for the Nonprofit book with autumn gifts" className="w-full rounded-lg shadow-2xl" style={{
              maxWidth: '450px'
            }} data-id="element-116" />
            </div>
          </div>
        </div>
      </div>
      {/* CSS for floating animation */}
      <style jsx data-id="element-117">{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          25% {
            transform: translateY(-20px) rotate(5deg);
          }
          50% {
            transform: translateY(-10px) rotate(-5deg);
          }
          75% {
            transform: translateY(-15px) rotate(3deg);
          }
        }
      `}</style>
    </section>;
}